import React, { MutableRefObject, useEffect, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// React Query
import { useMutation } from "@tanstack/react-query";

// MUI
import { GridColDef, GridRowParams, GridRowSelectionModel, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

// Notistack
import { SnackbarKey, useSnackbar } from "notistack";

// API
import { retrieveEmployee, sendUsers } from "@DataPages/api/users";

// Helpers
import RowFeedback from "@Common/helpers/RowFeedback";
import { defaultCellRenderer } from "@/Layouts/components/Tables/DataSmartTable/helpers";

// Components
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { SnackbarsActions, SnackbarsContent } from "@Layouts/components/Snackbars";

// Icons
import welcomeEmailIcon from "@/assets/icons/dataPages/users/resend-welcome-email.svg";
import welcomeEmailDisabledIcon from "@/assets/icons/dataPages/users/resend-welcome-email-disabled.svg";
import welcomeEmailIconWhite from "@/assets/icons/dataPages/users/resend-welcome-email-white.svg";
import addUserIcon from "@/assets/icons/dataPages/users/add-user-icon.svg";
import addUserDisabledIcon from "@/assets/icons/dataPages/users/add-user-icon-disabled.svg";
import addUserIconWhite from "@/assets/icons/dataPages/users/add-user-icon-white.svg";

// Definitions
import { IUser } from "@/Common/interfaces/user";
import { ICommunications, IRowData, IUserPageTableProps, IUserToCreate } from "@DataPages/types/users/users.d";
import { getFormatDate, getFormatUTCDate } from "@/Common/helpers/Dates";
import { tableKeys } from "@/Constants/tableKeys";
import { DATE_FORMAT } from "@/Constants/date";

const SendEmailIcon: React.FC<{ disabled: boolean }> = ({ disabled }) => (
	<img src={disabled ? welcomeEmailDisabledIcon : welcomeEmailIcon} width={23} height={23} alt="Send Email" />
);
const SendEmailIconWhite: React.FC = () => <img src={welcomeEmailIconWhite} width={23} height={23} alt="Send Email Blue" />;
const AddUserIcon: React.FC<{ disabled: boolean }> = ({ disabled }) => (
	<img src={disabled ? addUserDisabledIcon : addUserIcon} width={23} height={23} alt="Create user" />
);
const AddUserIconWhite: React.FC = () => <img src={addUserIconWhite} width={23} height={23} alt="Create user" />;

const UserPageTable: React.FC<IUserPageTableProps> = (props) => {
	const { enqueueSnackbar } = useSnackbar();
	const apiRef: MutableRefObject<GridApiPremium> = useGridApiRef();
	const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
	const [emailsToSend, setEmailsToSend] = useState<string[]>([]);
	const [rows, setRows] = useState<IRowData[] | undefined>(props.data);
	const isUser = props.selectionType !== "Unenrolled";
	const userLogged = useSelector((state: { user: { user: IUser } }) => state.user.user);
	const { mutate: updateUsers } = useMutation({
		mutationFn: (params: { locationId: string; newUsers: IUserToCreate[] }) => {
			return sendUsers(params.locationId, { activeUsers: params.newUsers, inactiveUsers: [] });
		},
		onSuccess: (_, variables) => {
			setRows((prev) => prev?.filter((row) => !variables.newUsers.some((user) => user._id === row._id)));
			props.setIsLoadingMutations(false);
			RowFeedback?.skeleton(_, _)?.successDelete(true);
		},
		onError: (error: Error) => {
			const message = error.message?.replace("Error: ", "") || "Unknown error";
			const renderSnackbarsActions = (key: SnackbarKey) => {
				return <SnackbarsActions key={key.toString()} index={key} />;
			};
			selectedIds.forEach((id) => {
				RowFeedback?.skeleton(id, apiRef)?.errorDelete();
				enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
					variant: "error",
					preventDuplicate: true,
					action: renderSnackbarsActions,
				});
			});
			props.setIsLoadingMutations(false);
		},
		onMutate: () => {
			props.setIsLoadingMutations(true);
		},
	});

	const headerClassName = "table-header-top-aligned show-as-column";
	const cellClassName = "table-small-cell user-page-table-cell";
	const columns: GridColDef[] = [
		{
			field: "_id",
			headerName: "ID",
			filterable: false,
		},
		{
			field: "fullName",
			headerName: "Name",
			width: 150,
			headerClassName,
			cellClassName,
			renderCell: defaultCellRenderer,
		},
		{
			field: "employeeId",
			headerName: "Emp. Id",
			width: 110,
			headerClassName,
			cellClassName,
			renderCell: defaultCellRenderer,
		},
		{
			field: "emailBusiness",
			headerName: "Business Email",
			width: 210,
			headerClassName,
			cellClassName,
			renderCell: defaultCellRenderer,
		},
		{
			field: "emailPersonal",
			headerName: "Personal Email",
			width: 210,
			headerClassName,
			cellClassName,
			renderCell: defaultCellRenderer,
		},
		{
			headerClassName,
			cellClassName,
			field: "hireDate",
			headerName: "Hire Date",
			width: 130,
			type: "date",
			valueFormatter: (value: string) => getFormatUTCDate(value, DATE_FORMAT.monthAbbrDatePadded),
			valueGetter: (value: string) => getFormatUTCDate(value, DATE_FORMAT.monthAbbrDatePadded),
			renderCell: defaultCellRenderer,
		},
	];

	if (props.selectionType !== "Unenrolled") {
		columns.push(
			{
				headerClassName,
				cellClassName,
				field: "lastLoginDateTime",
				headerName: "Last Login",
				width: 130,
				type: "date",
				valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.abbrDatePaddedTime12h),
				valueGetter: (value) => getFormatDate(value, DATE_FORMAT.abbrDatePaddedTime12h),
				renderCell: defaultCellRenderer,
			},
			{
				field: "accountStatus",
				headerName: "Acc. Status",
				width: 120,
				headerClassName,
				cellClassName,
				renderCell: defaultCellRenderer,
			}
		);
	}

	const handleSendConfirmationEmailCognitoUsers = () => {
		selectedIds.forEach((rowId) => {
			setTimeout(() => RowFeedback?.skeleton(rowId, apiRef)?.delete()?.display());
		});
		const waitingFeedBack = 3000;
		setTimeout(() => {
			props.handleSendConfirmationEmailCognitoUsers(emailsToSend);
			setSelectedIds([]);
		}, waitingFeedBack);
	};

	const handleAddUsers = async () => {
		props.setIsLoadingMutations(true);
		selectedIds.forEach((rowId) => {
			setTimeout(() => RowFeedback?.skeleton(rowId, apiRef)?.delete()?.display());
		});

		const newUsers: IUserToCreate[] = [];
		for (const item of selectedIds) {
			const dataEmployee = rows?.find((entry) => entry._id === item) as IRowData;
			const employee = await retrieveEmployee(dataEmployee.locationId, dataEmployee.employeeId);
			if (employee) {
				const emailBusiness = employee.communications.find(
					(element: ICommunications) => element.communicationType === "EMAIL" && element.usage === "BUSINESS"
				);
				if (emailBusiness) {
					newUsers.push({
						_id: item,
						update: {
							locationId: employee.locationId,
							username: emailBusiness.value,
							employeeId: employee.employeeId,
							fullName: employee.fullName,
							birthDate: employee.birthDate,
							communications: employee.communications,
							payrollSource: employee.payrollSource,
							payrollSourceId: employee.payrollSourceId,
							position: employee.jobTitle,
							payrollPosition: employee.jobTitle,
							supervisorSource: employee.payrollSource,
							supervisorId: employee.supervisorId,
							longevityDate: employee.currentStatus?.effectiveDate,
							currentStatus: {
								status: "ACTIVE",
								statusReason: employee.currentStatus?.statusReason,
								effectiveTimestamp: employee.currentStatus?.effectiveDate,
							},
							lastModifiedBy: userLogged._id,
							lastModifiedOn: new Date(),
							customFields: employee.customFields,
						},
					});
				}
			}
		}

		if (newUsers.length > 0) {
			updateUsers({
				locationId: newUsers[0].update?.locationId ?? "",
				newUsers,
			});
		}
	};

	const handleUserSelect = (rowsIds: GridRowSelectionModel) => {
		const emails: string[] = [];
		rowsIds.forEach((id) => {
			const row = apiRef.current.getRow(id) as IRowData;
			if (row) {
				emails.push(row.emailBusiness);
			}
		});
		setEmailsToSend(emails);
		setSelectedIds(rowsIds);
	};

	const handleUnenrolledSelect = (rowsIds: GridRowSelectionModel) => {
		setSelectedIds(rowsIds);
	};

	useEffect(() => {
		setRows(props.data);
	}, [props.data]);

	const buttonClasses =
		"!h-[30px] !p-[6px] !px-[12px] !bg-primary !text-white !border !border-primary !normal-case hover:drop-shadow-subtle-primary";
	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.USERS,
			}}
			apiRef={apiRef}
			columns={columns}
			editMode="row"
			rows={rows || []}
			maxRows={18}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			isRowSelectable={(params: GridRowParams<GridValidRowModel>): boolean =>
				params.row.accountStatus !== "Confirmed" && !props.isLoadingMutations
			}
			onRowSelectionModelChange={(rowIds) => (isUser ? handleUserSelect(rowIds) : handleUnenrolledSelect(rowIds))}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					filter: {
						hide: false,
					},
					search: {
						useQuickFilter: true,
					},
					confirm: {
						hide: false,
						disabled: !selectedIds.length,
						icon: isUser ? <SendEmailIcon disabled={!selectedIds.length} /> : <AddUserIcon disabled={!selectedIds.length} />,
						iconConfirm: isUser ? <SendEmailIconWhite /> : <AddUserIconWhite />,
						textConfirm: isUser ? "Resend Welcome Email" : "Add User",
						classes: isUser ? `!w-[172px] ${buttonClasses}` : `!w-[100px] ${buttonClasses}`,
						textClasses: "!text-[14.5px] !font-sans",
						onConfirmAction: isUser ? handleSendConfirmationEmailCognitoUsers : handleAddUsers,
					},
					sxActions: { margin: "-15px -40px" },
				},
				noRowsOverlay: {
					title: "No Users Data",
				},
			}}
			hideFooter
			customProps={{
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
				},
				expandableRows: false,
				hide: false,
			}}
		/>
	);
};

export default UserPageTable;
